import { useState, useEffect, useRef, useMemo } from 'react'
import { BottomSheet } from '@/shared/ui/bottom-sheet'
import { SelectionChip, SelectionGrid } from '@/shared/ui/selection-list'
import { formatPrice, formatWeightShort, CAKE_WEIGHT } from '@/shared/lib/format'
import { useTelegram } from '@/shared/lib/telegram'
import { cn } from '@/shared/lib/utils'

export interface WeightOption {
  weight: number
  price: number
  available?: boolean
}

export interface ProductWeightSelectorSheetProps {
  isOpen: boolean
  onClose: () => void
  weights: WeightOption[]
  selectedWeight?: number
  onSelect: (weight: number) => void
  /**
   * Заголовок bottom sheet
   * @default "Выберите объём"
   */
  title?: string
  /**
   * Цена за 1 грамм (для расчёта произвольного веса)
   */
  pricePerGram?: number
  /**
   * Разрешить ввод произвольного веса
   * @default true
   */
  allowCustomWeight?: boolean
  /**
   * Пользовательский вес блина (null = 357г по умолчанию)
   */
  cakeWeight?: number | null
}

// Лимиты для ввода веса
const MIN_WEIGHT = 5
const MAX_WEIGHT = 500

/**
 * Bottom Sheet для выбора веса чая
 * 
 * Унифицированный компонент — одинаковый вид на странице товара и в корзине.
 * Компактный: заголовок + 3 кнопки в ряд + опционально ввод произвольного веса.
 */
export function ProductWeightSelectorSheet({
  isOpen,
  onClose,
  weights,
  selectedWeight,
  onSelect,
  title = "Выберите объём",
  pricePerGram,
  allowCustomWeight = true,
  cakeWeight,
}: ProductWeightSelectorSheetProps) {
  const { hapticFeedback } = useTelegram()
  const [customWeight, setCustomWeight] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const wasOpenRef = useRef(false)
  
  const availableWeights = weights.filter(w => w.available !== false)
  
  // Мемоизируем стандартные веса для стабильного сравнения
  const standardWeights = useMemo(
    () => availableWeights.map(w => w.weight),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weights]
  )
  
  // Инициализация при открытии: если текущий вес нестандартный — показываем в поле ввода
  // Срабатывает ТОЛЬКО при переходе из закрытого в открытое состояние
  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current
    wasOpenRef.current = isOpen
    
    if (justOpened) {
      if (selectedWeight && !standardWeights.includes(selectedWeight)) {
        setCustomWeight(String(selectedWeight))
      } else {
        setCustomWeight('')
      }
      setIsFocused(false)
    }
  }, [isOpen, selectedWeight, standardWeights])
  
  if (availableWeights.length === 0) return null
  
  // Вычисляем цену за грамм из первой опции если не передана
  const calculatedPricePerGram = pricePerGram ?? (availableWeights[0]?.price / availableWeights[0]?.weight)
  
  const handleSelect = (weight: number) => {
    hapticFeedback.selectionChanged()
    onSelect(weight)
    onClose()
  }
  
  // Парсинг и валидация введённого веса
  const parsedCustomWeight = parseInt(customWeight, 10)
  const isValidCustomWeight = !isNaN(parsedCustomWeight) && 
    parsedCustomWeight >= MIN_WEIGHT && 
    parsedCustomWeight <= MAX_WEIGHT
  const customPrice = isValidCustomWeight ? calculatedPricePerGram * parsedCustomWeight : 0
  
  // Проверяем, совпадает ли введённый вес со стандартным
  const isStandardWeight = availableWeights.some(w => w.weight === parsedCustomWeight)
  
  // Текущий вес нестандартный и совпадает с введённым
  const isCurrentCustomWeight = !!(selectedWeight && 
    !standardWeights.includes(selectedWeight) && 
    parsedCustomWeight === selectedWeight)
  
  const handleCustomSubmit = () => {
    if (isValidCustomWeight && !isStandardWeight) {
      hapticFeedback.impactOccurred('medium')
      onSelect(parsedCustomWeight)
      onClose()
    }
  }
  
  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      fitContent
    >
      <div className="px-4 pt-2 pb-8 space-y-4">
        {/* Стандартные веса */}
        <SelectionGrid columns={availableWeights.length > 3 ? 4 : 3}>
          {availableWeights.map((option) => {
            const effectiveCakeWeight = cakeWeight ?? CAKE_WEIGHT
            const isCake = option.weight === effectiveCakeWeight
            return (
              <SelectionChip
                key={option.weight}
                selected={selectedWeight === option.weight}
                onClick={() => handleSelect(option.weight)}
                label={isCake ? `Блин (${effectiveCakeWeight}г)` : `${option.weight} г`}
                sublabel={formatPrice(option.price)}
              />
            )
          })}
        </SelectionGrid>
        
        {/* Ввод произвольного веса */}
        {allowCustomWeight && calculatedPricePerGram > 0 && (
          <div className="pt-2">
            {/* Разделитель */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-gray-200 dark:bg-dark-border" />
              <span className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide">
                или свой вес
              </span>
              <div className="flex-1 h-px bg-gray-200 dark:bg-dark-border" />
            </div>
            
            {/* Поле ввода с кнопкой */}
            <div className="flex items-center gap-3">
              {/* Поле ввода */}
              <div className={cn(
                "flex-1 flex items-center gap-2 px-4 py-3 rounded-xl border-2 transition-colors",
                isFocused || isCurrentCustomWeight
                  ? "border-primary dark:border-primary-light bg-primary/5 dark:bg-primary/10" 
                  : "border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card"
              )}>
                <input
                  type="number"
                  inputMode="numeric"
                  placeholder={`${MIN_WEIGHT}–${MAX_WEIGHT}`}
                  value={customWeight}
                  onChange={(e) => setCustomWeight(e.target.value)}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  className={cn(
                    "flex-1 bg-transparent outline-none text-base font-medium tabular-nums",
                    "text-ink dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500",
                    "w-full min-w-0"
                  )}
                  min={MIN_WEIGHT}
                  max={MAX_WEIGHT}
                />
                <span className="text-sm text-gray-400 dark:text-gray-500 flex-shrink-0">г</span>
              </div>
              
              {/* Цена и кнопка */}
              <button
                type="button"
                onClick={handleCustomSubmit}
                disabled={!isValidCustomWeight || isStandardWeight || isCurrentCustomWeight}
                className={cn(
                  "flex flex-col items-center justify-center px-4 py-2 rounded-xl transition-all min-w-[90px]",
                  isCurrentCustomWeight
                    ? "bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary-light"
                    : isValidCustomWeight && !isStandardWeight
                      ? "bg-primary dark:bg-primary-dark text-white active:scale-95"
                      : "bg-gray-100 dark:bg-dark-elevated text-gray-400 dark:text-gray-500"
                )}
              >
                <span className="text-sm font-semibold">
                  {isValidCustomWeight ? formatPrice(customPrice) : '—'}
                </span>
                <span className="text-[10px] opacity-80">
                  {isCurrentCustomWeight ? 'текущий' : isStandardWeight ? 'выше ↑' : 'выбрать'}
                </span>
              </button>
            </div>
            
            {/* Подсказка */}
            {customWeight && !isValidCustomWeight && (
              <p className="text-xs text-error mt-2">
                Введите от {MIN_WEIGHT} до {MAX_WEIGHT} грамм
              </p>
            )}
          </div>
        )}
      </div>
    </BottomSheet>
  )
}
